import { forwardRef, useImperativeHandle } from 'react';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';
import Tabs from 'components/partials/tabs/tabs';

import AdmitAdjustmentsTab from './components/admit-adjustments-tab';
import CoaModal from './components/coa-modal';
import CompareEstimatesModal from './components/compare-estimates-modal';
import MetricsSection from './components/metrics-section';
import NonModeledTab from './components/non-modeled-tab';
import OverviewTab from './components/overview-tab';
import { useAdmitAdjustments } from './hooks/use-admit-adjustments';
import { useNonModeledAdjustments } from './hooks/use-non-modeled-adjustments';
import { useResultsStep } from './hooks/use-results-step';
import { RESULTS_TABS } from './results-step.constants';

interface ResultsStepProps {
  clientId?: string | number;
  readOnly?: boolean;
}

const ResultsStep = forwardRef<unknown, ResultsStepProps>(
  ({ clientId, readOnly }, ref) => {
    const admitAdjustments = useAdmitAdjustments();

    const {
      modelId,
      isLoading,
      isError,
      errorMessage,
      hasData,
      activeTab,
      setActiveTab,
      showNetComprehensiveFee,
      setShowNetComprehensiveFee,
      metricCards,
      actualYearLabel,
      overviewRows,
      nonModeledRows,
      showModeledActualYear,
      setShowModeledActualYear,
      showLastYear,
      setShowLastYear,
      isCoaOpen,
      openCoa,
      closeCoa,
      coaGroups,
      isCoaLoading,
      compareEstimates,
      downloadDataset,
      isDownloadingDataset,
      canDownloadDataset,
    } = useResultsStep({
      clientId,
      previewTotalFirstYear: admitAdjustments.isDirtyGlobal
        ? admitAdjustments.previewResults?.estimatedResults?.totalFirstYear
        : undefined,
    });
    const nonModeledAdjustments = useNonModeledAdjustments(nonModeledRows);

    useImperativeHandle(
      ref,
      () => ({
        hasChanges:
          admitAdjustments.isDirtyGlobal || nonModeledAdjustments.isDirtyGlobal,
        save: () => {
          admitAdjustments.handleSaveDraft();
          nonModeledAdjustments.handleSaveDraft();
        },
        discard: () => {
          admitAdjustments.handleRevertAll();
          nonModeledAdjustments.handleRevertAll();
        },
      }),
      [
        admitAdjustments.isDirtyGlobal,
        admitAdjustments.handleSaveDraft,
        admitAdjustments.handleRevertAll,
        nonModeledAdjustments.isDirtyGlobal,
        nonModeledAdjustments.handleSaveDraft,
        nonModeledAdjustments.handleRevertAll,
      ]
    );

    if (!modelId) {
      return (
        <Box sx={{ py: 4, textAlign: 'center', color: 'tokens.gray.600' }}>
          Save the model first to view results.
        </Box>
      );
    }

    if (isError) {
      return (
        <Box sx={{ py: 4, textAlign: 'center', color: 'tokens.critical.600' }}>
          {errorMessage}
        </Box>
      );
    }

    return (
      <Box
        data-testid="results-step"
        sx={{ position: 'relative', minHeight: '400px' }}
      >
        <Loader
          loading={isLoading}
          fullScreen={false}
          backdrop="transparent"
          overlay="transparent"
        />

        {!isLoading && hasData && (
          <Stack sx={{ gap: 3 }}>
            <MetricsSection
              cards={metricCards}
              showNetComprehensiveFee={showNetComprehensiveFee}
              onNetComprehensiveFeeChange={setShowNetComprehensiveFee}
              actualYearLabel={actualYearLabel}
              isRecalculating={admitAdjustments.isPreviewing}
              onShowCoa={openCoa}
              isCoaDisabled={isCoaLoading}
              onCompareEstimates={compareEstimates.open}
              canCompare={compareEstimates.canCompare}
              onDownloadDataset={downloadDataset}
              isDownloadingDataset={isDownloadingDataset}
              canDownloadDataset={canDownloadDataset}
            />

            <Tabs
              items={RESULTS_TABS}
              activeTab={activeTab}
              onSelect={(value) => setActiveTab(value as typeof activeTab)}
              data-testid="results-tabs"
            />

            {activeTab === 'overview' && (
              <OverviewTab
                rows={overviewRows}
                showModeledActualYear={showModeledActualYear}
                onShowModeledActualYearChange={setShowModeledActualYear}
              />
            )}

            {activeTab === 'non-modeled' && (
              <NonModeledTab
                nonModeledAdjustments={nonModeledAdjustments}
                showLastYear={showLastYear}
                onShowLastYearChange={setShowLastYear}
                readOnly={readOnly}
              />
            )}

            {activeTab === 'admit-adjustments' && (
              <AdmitAdjustmentsTab
                admitAdjustments={admitAdjustments}
                clientId={clientId}
                readOnly={readOnly}
              />
            )}
          </Stack>
        )}

        {!isLoading && !hasData && !isError && (
          <div className="py-8 text-center text-gray-600">
            No results are available for this model yet.
          </div>
        )}

        {isCoaOpen && (
          <CoaModal
            coaGroups={coaGroups}
            isLoading={isCoaLoading}
            onClose={closeCoa}
            previousYearLabel="Current year"
            currentYearLabel="Future year"
          />
        )}

        {compareEstimates.isOpen && (
          <CompareEstimatesModal
            currentRow={compareEstimates.currentRow}
            comparisonRowStates={compareEstimates.comparisonRowStates}
            versionOptions={compareEstimates.versionOptions}
            onSelectVersion={compareEstimates.setRowVersionId}
            onAddRow={compareEstimates.addRow}
            onRemoveRow={compareEstimates.removeRow}
            canAddRow={compareEstimates.canAddRow}
            userId={compareEstimates.userId}
            onClose={compareEstimates.close}
          />
        )}
      </Box>
    );
  }
);

ResultsStep.displayName = 'ResultsStep';

export default ResultsStep;
